/**
 * Wrapper de ruta protegida. Verifica autenticación.
 */
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/shared/feedback/Feedback';
import { MainLayout } from '@/components/shared/layout/MainLayout/MainLayout';
import { TenantContextSelector } from '@/components/shared/layout/TenantContextSelector';
import type { ReactNode } from 'react';
import type { Crumb } from '@/components/shared/navigation/Breadcrumb';

type Props = { children: ReactNode; crumbs?: Crumb[]; requiredPermission?: string; requiredAnyPermissions?: readonly string[]; rightSlot?: ReactNode };

const PLATFORM_ROUTE_PREFIXES = [
  '/app/tenants',
  '/app/platform-identities',
  '/app/platform-roles',
  '/app/platform-permissions',
  '/app/audit-logs',
  '/app/general-catalogs',
  '/app/general-settings',
  '/app/process-rules',
  // El catalogo global de acciones del ERP (ADR-010) es de plataforma: se
  // administra sin tenant activo. Las demas rutas de workflow son operativas y
  // por eso exigen contexto.
  '/app/platform-workflow-actions',
  '/app/business-rules',
  '/app/profile',
] as const;

export function ProtectedRoute({ children, requiredPermission, requiredAnyPermissions, rightSlot }: Props) {
  const { isAuthenticated, isLoading, hasPermission, user } = useAuth();
  const location = useLocation();

  if (isLoading) return <LoadingSpinner size="lg" />;
  if (!isAuthenticated) return <Navigate to="/login" state={{ from: location }} replace />;
  // La credencial temporal NO secuestra la navegación: el backend bloquea las
  // escrituras de negocio y el Header muestra un aviso con la acción. Antes
  // esta redirección forzaba a `/app/profile` con dos tabs deshabilitados, y la
  // persona no entendía por qué la aplicación estaba medio muerta.
  const isPlatformRoute = PLATFORM_ROUTE_PREFIXES.some((path) => location.pathname === path || location.pathname.startsWith(`${path}/`));
  if (!user?.active_tenant_id && !isPlatformRoute) {
    return <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4"><TenantContextSelector required /></div>;
  }
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/app/dashboard" replace />;
  }
  if (requiredAnyPermissions && !requiredAnyPermissions.some(hasPermission)) {
    return <Navigate to="/app/dashboard" replace />;
  }

  return <MainLayout rightSlot={rightSlot}>{children}</MainLayout>;
}
